import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Image, Search, RefreshCw, Check, X, Send, Loader,
  AlertTriangle, CheckCircle, FileText, Zap, Filter,
} from "lucide-react";
import api from "../api/client";

function ImageFixRow({ fix, siteId, onUpdate }) {
  const [pushing, setPushing] = useState(false);

  const handleApprove = () => {
    api.post(`/websites/${siteId}/alt-text/fixes/${fix.id}/approve/`)
      .then(() => onUpdate());
  };

  const handleReject = () => {
    api.post(`/websites/${siteId}/alt-text/fixes/${fix.id}/reject/`)
      .then(() => onUpdate());
  };

  const handlePush = () => {
    setPushing(true);
    api.post(`/websites/${siteId}/alt-text/fixes/${fix.id}/push/`)
      .then((res) => {
        if (res.status === 202 && res.data.task_id) {
          // Poll for completion
          const taskId = res.data.task_id;
          const poll = setInterval(() => {
            api.get(`/websites/${siteId}/alt-text/fixes/${fix.id}/status/?task_id=${taskId}`)
              .then((s) => {
                if (s.data.state === "complete") {
                  clearInterval(poll);
                  setPushing(false);
                  onUpdate();
                }
              })
              .catch(() => { clearInterval(poll); setPushing(false); });
          }, 2000);
          // Safety: stop polling after 2 minutes
          setTimeout(() => { clearInterval(poll); setPushing(false); onUpdate(); }, 120000);
        } else {
          onUpdate();
          setPushing(false);
        }
      })
      .catch(() => setPushing(false));
  };

  const isAlt = fix.fix_type === "alt_text";
  const isPushed = fix.status === "pushed";
  const isApproved = fix.status === "approved";

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
      <div className="flex gap-4">
        {/* Thumbnail */}
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-dark-50 flex items-center justify-center">
          {isAlt ? (
            <img
              src={fix.current_value}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; e.target.parentNode.innerHTML = '<div class="text-gray-600 flex items-center justify-center w-full h-full"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>'; }}
            />
          ) : (
            <Image size={24} className="text-gray-600" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="text-xs text-gray-500 font-mono truncate">
            {fix.page_url}
          </div>

          {isAlt ? (
            <>
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-500 w-16 flex-shrink-0 pt-0.5">Current:</span>
                <span className={`text-sm ${fix.current_value && !fix.current_value.startsWith("http") ? "text-gray-300" : "text-red-400 italic"}`}>
                  {fix.current_value && !fix.current_value.startsWith("http") ? fix.current_value : "Missing alt text"}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-500 w-16 flex-shrink-0 pt-0.5">Suggested:</span>
                <span className="text-sm text-green-400">{fix.suggested_value}</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-500 w-16 flex-shrink-0 pt-0.5">Current:</span>
                <span className="text-sm text-red-400 font-mono">{fix.current_value.split("/").pop()}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-500 w-16 flex-shrink-0 pt-0.5">Suggested:</span>
                <span className="text-sm text-green-400 font-mono">{fix.suggested_value}</span>
              </div>
            </>
          )}

          {fix.ai_reasoning && (
            <div className="text-xs text-gray-500">{fix.ai_reasoning}</div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col items-center gap-2 flex-shrink-0">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            isAlt ? "bg-blue-900/20 text-blue-400" : "bg-amber-900/20 text-amber-400"
          }`}>
            {isAlt ? "Alt Text" : "Filename"}
          </span>

          {isPushed ? (
            <span className="flex items-center gap-1 text-xs text-green-400">
              <CheckCircle size={12} /> Pushed
            </span>
          ) : isApproved ? (
            <button
              onClick={handlePush}
              disabled={pushing}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-copper/20 text-copper rounded hover:bg-copper/30 disabled:opacity-50"
            >
              {pushing ? <Loader size={11} className="animate-spin" /> : <Send size={11} />}
              Push to WP
            </button>
          ) : fix.status === "suggested" ? (
            <div className="flex gap-1">
              <button
                onClick={handleApprove}
                className="p-1.5 text-green-400 hover:bg-green-900/30 rounded"
                title="Approve"
              >
                <Check size={14} />
              </button>
              <button
                onClick={handleReject}
                className="p-1.5 text-red-400 hover:bg-red-900/30 rounded"
                title="Reject"
              >
                <X size={14} />
              </button>
            </div>
          ) : fix.status === "rejected" ? (
            <span className="text-xs text-red-400">Rejected</span>
          ) : (
            <span className="text-xs text-gray-500">{fix.status}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ScanResultRow({ img }) {
  const filename = img.filename || img.src.split("/").pop();
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
      <div className="flex gap-4">
        <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-dark-50">
          <img
            src={img.src}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-500 font-mono truncate mb-1">{filename}</div>
          <div className="flex items-center gap-3">
            {img.has_alt ? (
              <span className="flex items-center gap-1 text-xs text-green-400">
                <CheckCircle size={11} /> Alt: &quot;{img.current_alt}&quot;
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-red-400">
                <AlertTriangle size={11} /> Missing alt text
              </span>
            )}
            {img.bad_filename && (
              <span className="flex items-center gap-1 text-xs text-amber-400">
                <AlertTriangle size={11} /> Bad filename
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AltTextGenerator({ selectedSite }) {
  const [tab, setTab] = useState("fixes"); // fixes | scan | generate
  const [fixes, setFixes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fix queue filters
  const [fixStatusFilter, setFixStatusFilter] = useState("all");
  const [fixTypeFilter, setFixTypeFilter] = useState("all"); // all | alt_text | filename

  // Scan state
  const [scanUrl, setScanUrl] = useState("");
  const [scanResults, setScanResults] = useState(null);
  const [scanning, setScanning] = useState(false);

  // Scan all state
  const [scanAllProgress, setScanAllProgress] = useState(null);
  const scanAllTaskRef = useRef(null);
  const scanAllPollRef = useRef(null);

  // Generate all state (step 2: Claude Vision)
  const [genAllProgress, setGenAllProgress] = useState(null);
  const genAllTaskRef = useRef(null);
  const genAllPollRef = useRef(null);
  const [visionCostInfo, setVisionCostInfo] = useState(null);

  // Generate state
  const [genUrl, setGenUrl] = useState("");
  const [genKeyword, setGenKeyword] = useState("");
  const [genResults, setGenResults] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Bulk actions
  const [bulkAction, setBulkAction] = useState("");

  const fetchFixes = () => {
    if (!selectedSite) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${selectedSite.id}/alt-text/fixes/`)
        .then((r) => setFixes(r.data.fixes || []))
        .catch(() => setFixes([])),
      api.get(`/websites/${selectedSite.id}/alt-text/summary/`)
        .then((r) => setSummary(r.data))
        .catch(() => setSummary(null)),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { fetchFixes(); }, [selectedSite]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (scanAllPollRef.current) clearInterval(scanAllPollRef.current);
      if (genAllPollRef.current) clearInterval(genAllPollRef.current);
    };
  }, []);

  const handleScanPage = () => {
    if (!scanUrl.trim()) return;
    setScanning(true);
    setScanResults(null);
    api.post(`/websites/${selectedSite.id}/alt-text/scan-page/`, { url: scanUrl.trim() })
      .then((r) => setScanResults(r.data))
      .catch(() => setScanResults(null))
      .finally(() => setScanning(false));
  };

  const handleScanAll = () => {
    setScanAllProgress({ pages_scanned: 0, pages_total: 0, images_found: 0, fixes_created: 0, status: "starting" });
    api.post(`/websites/${selectedSite.id}/alt-text/scan-all/`)
      .then((r) => {
        const taskId = r.data.task_id;
        scanAllTaskRef.current = taskId;
        // Start polling
        scanAllPollRef.current = setInterval(() => {
          api.get(`/websites/${selectedSite.id}/alt-text/scan-all/status/`, { params: { task_id: taskId } })
            .then((res) => {
              setScanAllProgress(res.data);
              if (res.data.status === "complete" || res.data.status === "failed") {
                clearInterval(scanAllPollRef.current);
                scanAllPollRef.current = null;
                fetchFixes();
              }
            })
            .catch(() => {});
        }, 3000);
      })
      .catch(() => setScanAllProgress(null));
  };

  const handleGenerateAll = (force = false) => {
    setVisionCostInfo(null);
    setGenAllProgress({ generated: 0, total: 0, status: "starting" });
    api.post(`/websites/${selectedSite.id}/alt-text/generate-all/`, { force })
      .then((r) => {
        if (r.data.status === "vision_disabled") {
          // Show cost warning
          setGenAllProgress(null);
          setVisionCostInfo(r.data);
          return;
        }
        const taskId = r.data.task_id;
        genAllTaskRef.current = taskId;
        genAllPollRef.current = setInterval(() => {
          api.get(`/websites/${selectedSite.id}/alt-text/scan-all/status/`, { params: { task_id: taskId } })
            .then((res) => {
              setGenAllProgress(res.data);
              if (res.data.status === "complete" || res.data.status === "failed") {
                clearInterval(genAllPollRef.current);
                genAllPollRef.current = null;
                fetchFixes();
              }
            })
            .catch(() => {});
        }, 3000);
      })
      .catch(() => setGenAllProgress(null));
  };

  const handleGenerate = () => {
    if (!genUrl.trim()) return;
    setGenerating(true);
    setGenResults(null);
    api.post(`/websites/${selectedSite.id}/alt-text/generate/`, {
      url: genUrl.trim(),
      keyword: genKeyword.trim(),
    })
      .then((r) => setGenResults(r.data))
      .catch(() => setGenResults(null))
      .finally(() => setGenerating(false));
  };

  const handleSaveGenerated = () => {
    if (!genResults || !genResults.results) return;
    setSaving(true);

    const fixesToCreate = [];
    for (const r of genResults.results) {
      if (r.suggested_alt_text) {
        fixesToCreate.push({
          fix_type: "alt_text",
          current_value: r.image_url,
          suggested_value: r.suggested_alt_text,
          reasoning: r.reasoning || "AI-generated alt text",
        });
      }
      if (r.suggested_filename) {
        fixesToCreate.push({
          fix_type: "filename",
          current_value: r.image_url,
          suggested_value: r.suggested_filename,
          reasoning: `Filename issues: ${(r.filename_issues || []).join(", ")}`,
        });
      }
    }

    api.post(`/websites/${selectedSite.id}/alt-text/create-fixes/`, {
      page_url: genResults.url,
      fixes: fixesToCreate,
    })
      .then(() => {
        fetchFixes();
        setTab("fixes");
      })
      .finally(() => setSaving(false));
  };

  const handleBulkApprove = (types) => {
    setBulkAction("approving");
    api.post(`/websites/${selectedSite.id}/alt-text/bulk-approve/`, { fix_types: types })
      .then(() => fetchFixes())
      .finally(() => setBulkAction(""));
  };

  const handleBulkPush = (types) => {
    setBulkAction("pushing");
    api.post(`/websites/${selectedSite.id}/alt-text/bulk-push/`, { fix_types: types })
      .then((res) => {
        if (res.status === 202 && res.data.task_id) {
          const taskId = res.data.task_id;
          const poll = setInterval(() => {
            api.get(`/websites/${selectedSite.id}/alt-text/bulk-push/status/?task_id=${taskId}`)
              .then((s) => {
                if (s.data.state === "complete") {
                  clearInterval(poll);
                  setBulkAction("");
                  fetchFixes();
                }
              })
              .catch(() => { clearInterval(poll); setBulkAction(""); });
          }, 3000);
          setTimeout(() => { clearInterval(poll); setBulkAction(""); fetchFixes(); }, 300000);
        } else {
          fetchFixes();
          setBulkAction("");
        }
      })
      .catch(() => setBulkAction(""));
  };

  // Clicking stat cards sets filters and switches to fixes tab
  const handleStatClick = (filter) => {
    setTab("fixes");
    if (filter === "missing") {
      setFixStatusFilter("suggested");
      setFixTypeFilter("alt_text");
    } else if (filter === "suggested") {
      setFixStatusFilter("suggested");
      setFixTypeFilter("all");
    } else if (filter === "pushed") {
      setFixStatusFilter("pushed");
      setFixTypeFilter("all");
    } else {
      setFixStatusFilter("all");
      setFixTypeFilter("all");
    }
  };

  // Filtered fixes
  const filteredFixes = useMemo(() => {
    let data = fixes.filter((f) => f.status !== "rejected");
    if (fixStatusFilter !== "all") {
      data = data.filter((f) => f.status === fixStatusFilter);
    }
    if (fixTypeFilter !== "all") {
      data = data.filter((f) => f.fix_type === fixTypeFilter);
    }
    return data;
  }, [fixes, fixStatusFilter, fixTypeFilter]);

  if (!selectedSite) {
    return <div className="text-gray-500 text-center py-20">Select a website</div>;
  }

  const suggestedFixes = fixes.filter((f) => f.status === "suggested");
  const approvedFixes = fixes.filter((f) => f.status === "approved");
  const isScanRunning = scanAllProgress && scanAllProgress.status !== "complete" && scanAllProgress.status !== "failed";
  const isGenRunning = genAllProgress && genAllProgress.status !== "complete" && genAllProgress.status !== "failed";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Image size={20} className="text-copper" /> Alt Text AI
        </h2>
        <div className="flex gap-2">
          <button
            onClick={handleScanAll}
            disabled={isScanRunning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-copper hover:bg-copper-600 text-white rounded-lg font-medium disabled:opacity-50"
          >
            <RefreshCw size={12} className={isScanRunning ? "animate-spin" : ""} />
            {isScanRunning ? "Scanning..." : "Scan All Pages"}
          </button>
        </div>
      </div>

      {/* Scan All Progress Bar */}
      {scanAllProgress && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-300 flex items-center gap-2">
              {isScanRunning && <Loader size={14} className="animate-spin text-copper" />}
              {scanAllProgress.status === "complete" ? (
                <span className="text-green-400 flex items-center gap-1"><CheckCircle size={14} /> Scan complete</span>
              ) : scanAllProgress.status === "failed" ? (
                <span className="text-red-400">Scan failed</span>
              ) : (
                `Scanning page ${scanAllProgress.pages_scanned} of ${scanAllProgress.pages_total}...`
              )}
            </span>
            <div className="flex gap-4 text-xs text-gray-500">
              <span>{scanAllProgress.images_found || 0} images</span>
              <span className="text-red-400">{scanAllProgress.missing_alt || 0} missing alt</span>
              <span>{scanAllProgress.fixes_created || 0} filename fixes</span>
            </div>
          </div>
          {scanAllProgress.pages_total > 0 && (
            <div className="w-full bg-dark-50 rounded-full h-2">
              <div
                className="bg-copper rounded-full h-2 transition-all duration-500"
                style={{ width: `${Math.round((scanAllProgress.pages_scanned / scanAllProgress.pages_total) * 100)}%` }}
              />
            </div>
          )}
          {scanAllProgress.status === "complete" && (
            <div className="flex items-center gap-3">
              {(scanAllProgress.missing_alt || 0) > 0 && !genAllProgress && (
                <button
                  onClick={() => handleGenerateAll()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-copper hover:bg-copper-600 text-white rounded-lg font-medium"
                >
                  <Zap size={12} /> Generate AI Alt Text ({scanAllProgress.missing_alt} images)
                </button>
              )}
              <button
                onClick={() => setScanAllProgress(null)}
                className="text-xs text-gray-500 hover:text-gray-300"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}

      {/* Vision Cost Warning */}
      {visionCostInfo && (
        <div className="bg-amber-900/10 border border-amber-700/30 rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <div className="text-sm text-amber-300 font-medium">Claude Vision is disabled</div>
              <div className="text-xs text-gray-400">
                {visionCostInfo.cost_warning}
              </div>
              <div className="text-xs text-gray-500">
                {visionCostInfo.images_needing_alt} images need alt text. Enable Vision in Settings or use text-only generation.
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setVisionCostInfo(null); handleGenerateAll(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-600/20 text-amber-400 rounded-lg hover:bg-amber-600/30 font-medium"
            >
              <Zap size={12} /> Use Vision Anyway (${visionCostInfo.estimated_cost})
            </button>
            <button
              onClick={() => setVisionCostInfo(null)}
              className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Generate All Progress */}
      {genAllProgress && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-300 flex items-center gap-2">
              {genAllProgress.status !== "complete" && genAllProgress.status !== "failed" && (
                <Loader size={14} className="animate-spin text-copper" />
              )}
              {genAllProgress.status === "complete" ? (
                <span className="text-green-400 flex items-center gap-1"><CheckCircle size={14} /> Generated {genAllProgress.generated} alt texts</span>
              ) : genAllProgress.status === "failed" ? (
                <span className="text-red-400">Generation failed</span>
              ) : (
                `Generating alt text... ${genAllProgress.generated || 0} of ${genAllProgress.total || 0}`
              )}
            </span>
          </div>
          {(genAllProgress.total || 0) > 0 && (
            <div className="w-full bg-dark-50 rounded-full h-2">
              <div
                className="bg-blue-500 rounded-full h-2 transition-all duration-500"
                style={{ width: `${Math.round(((genAllProgress.generated || 0) / genAllProgress.total) * 100)}%` }}
              />
            </div>
          )}
          {(genAllProgress.status === "complete" || genAllProgress.status === "failed") && (
            <button
              onClick={() => setGenAllProgress(null)}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {/* Summary Cards — clickable */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => handleStatClick("total")}
            className={`bg-dark-300 border rounded-lg p-3 text-center transition-colors ${
              fixStatusFilter === "all" && fixTypeFilter === "all" ? "border-copper" : "border-dark-50 hover:border-gray-600"
            }`}
          >
            <div className="text-lg font-mono font-bold">{summary.total_images}</div>
            <div className="text-xs text-gray-500">Total Images</div>
          </button>
          <button
            onClick={() => handleStatClick("missing")}
            className={`bg-dark-300 border rounded-lg p-3 text-center transition-colors ${
              fixStatusFilter === "suggested" && fixTypeFilter === "alt_text" ? "border-red-500" : "border-dark-50 hover:border-gray-600"
            }`}
          >
            <div className={`text-lg font-mono font-bold ${summary.missing_alt > 0 ? "text-red-400" : "text-green-400"}`}>
              {summary.missing_alt}
            </div>
            <div className="text-xs text-gray-500">Missing Alt Text</div>
          </button>
          <button
            onClick={() => handleStatClick("suggested")}
            className={`bg-dark-300 border rounded-lg p-3 text-center transition-colors ${
              fixStatusFilter === "suggested" && fixTypeFilter === "all" ? "border-blue-500" : "border-dark-50 hover:border-gray-600"
            }`}
          >
            <div className="text-lg font-mono font-bold text-blue-400">{summary.fixes_suggested}</div>
            <div className="text-xs text-gray-500">Suggestions</div>
          </button>
          <button
            onClick={() => handleStatClick("pushed")}
            className={`bg-dark-300 border rounded-lg p-3 text-center transition-colors ${
              fixStatusFilter === "pushed" ? "border-green-500" : "border-dark-50 hover:border-gray-600"
            }`}
          >
            <div className="text-lg font-mono font-bold text-green-400">{summary.fixes_pushed}</div>
            <div className="text-xs text-gray-500">Pushed to WP</div>
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-dark-50">
        {[
          { key: "fixes", label: "Fix Queue", count: suggestedFixes.length + approvedFixes.length },
          { key: "scan", label: "Scan Page" },
          { key: "generate", label: "Generate Alt Text" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-copper text-copper" : "border-transparent text-gray-400 hover:text-white"
            }`}
          >
            {t.label}
            {t.count > 0 && <span className="text-xs bg-dark-50 px-1.5 py-0.5 rounded">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "fixes" && (
        <div className="space-y-3">
          {/* Status filter pills */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1.5">
              {[
                { key: "all", label: "All" },
                { key: "suggested", label: "Suggested", count: suggestedFixes.length },
                { key: "approved", label: "Approved", count: approvedFixes.length },
                { key: "pushed", label: "Pushed" },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setFixStatusFilter(s.key)}
                  className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                    fixStatusFilter === s.key
                      ? "bg-copper/20 text-copper"
                      : "bg-dark-300 text-gray-400 hover:text-white"
                  }`}
                >
                  {s.label}
                  {s.count > 0 && <span className="text-[10px] bg-dark-50 px-1 rounded">{s.count}</span>}
                </button>
              ))}
              <span className="w-px bg-dark-50 mx-1" />
              {[
                { key: "all", label: "All Types" },
                { key: "alt_text", label: "Alt Text" },
                { key: "filename", label: "Filenames" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setFixTypeFilter(t.key)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                    fixTypeFilter === t.key
                      ? "bg-blue-900/20 text-blue-400"
                      : "bg-dark-300 text-gray-400 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk actions */}
          {(suggestedFixes.length > 0 || approvedFixes.length > 0) && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBulkApprove(["alt_text"])}
                disabled={!!bulkAction}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-900/20 text-blue-400 rounded-lg hover:bg-blue-900/30 disabled:opacity-50"
              >
                <Check size={12} /> Approve All Alt Text
              </button>
              <button
                onClick={() => handleBulkApprove(["filename"])}
                disabled={!!bulkAction}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-900/20 text-amber-400 rounded-lg hover:bg-amber-900/30 disabled:opacity-50"
              >
                <Check size={12} /> Approve All Filenames
              </button>
              <button
                onClick={() => handleBulkApprove(["alt_text", "filename"])}
                disabled={!!bulkAction}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 disabled:opacity-50"
              >
                <Check size={12} /> Approve All
              </button>
              {approvedFixes.length > 0 && (
                <button
                  onClick={() => handleBulkPush(["alt_text", "filename"])}
                  disabled={!!bulkAction}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-900/20 text-green-400 rounded-lg hover:bg-green-900/30 disabled:opacity-50"
                >
                  <Send size={12} /> {bulkAction === "pushing" ? "Pushing..." : `Push ${approvedFixes.length} Approved`}
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="p-8 text-center text-gray-500 animate-pulse">Loading fixes...</div>
          ) : filteredFixes.length === 0 ? (
            <div className="text-gray-500 text-center py-12">
              {fixes.length > 0
                ? "No fixes match your current filters."
                : 'No alt text or filename fixes yet. Use "Scan All Pages" or the "Generate" tab to find issues.'}
            </div>
          ) : (
            filteredFixes.map((fix) => (
              <ImageFixRow
                key={fix.id}
                fix={fix}
                siteId={selectedSite.id}
                onUpdate={fetchFixes}
              />
            ))
          )}

          {filteredFixes.length > 0 && (
            <div className="text-xs text-gray-500 text-center py-2">
              Showing {filteredFixes.length} of {fixes.filter((f) => f.status !== "rejected").length} fixes
            </div>
          )}
        </div>
      )}

      {tab === "scan" && (
        <div className="space-y-4">
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Page URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={scanUrl}
                  onChange={(e) => setScanUrl(e.target.value)}
                  placeholder="https://example.com/page"
                  className="flex-1 bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50"
                  onKeyDown={(e) => e.key === "Enter" && handleScanPage()}
                />
                <button
                  onClick={handleScanPage}
                  disabled={scanning || !scanUrl.trim()}
                  className="flex items-center gap-2 px-4 py-2.5 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                >
                  {scanning ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
                  Scan
                </button>
              </div>
            </div>
          </div>

          {scanResults && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-dark-300 border border-dark-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-mono font-bold">{scanResults.total_images}</div>
                  <div className="text-xs text-gray-500">Images Found</div>
                </div>
                <div className="bg-dark-300 border border-dark-50 rounded-lg p-3 text-center">
                  <div className={`text-lg font-mono font-bold ${scanResults.missing_alt > 0 ? "text-red-400" : "text-green-400"}`}>
                    {scanResults.missing_alt}
                  </div>
                  <div className="text-xs text-gray-500">Missing Alt</div>
                </div>
                <div className="bg-dark-300 border border-dark-50 rounded-lg p-3 text-center">
                  <div className={`text-lg font-mono font-bold ${scanResults.bad_filenames > 0 ? "text-amber-400" : "text-green-400"}`}>
                    {scanResults.bad_filenames}
                  </div>
                  <div className="text-xs text-gray-500">Bad Filenames</div>
                </div>
              </div>

              {(scanResults.missing_alt > 0 || scanResults.bad_filenames > 0) && (
                <button
                  onClick={() => {
                    setGenUrl(scanUrl);
                    setTab("generate");
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-copper/20 text-copper text-sm rounded-lg hover:bg-copper/30"
                >
                  <Zap size={14} /> Generate AI Suggestions
                </button>
              )}

              <div className="space-y-2">
                {(scanResults.images || []).map((img, i) => (
                  <ScanResultRow key={i} img={img} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "generate" && (
        <div className="space-y-4">
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Page URL</label>
              <input
                type="url"
                value={genUrl}
                onChange={(e) => setGenUrl(e.target.value)}
                placeholder="https://example.com/page"
                className="w-full bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Target Keyword (optional)</label>
              <input
                type="text"
                value={genKeyword}
                onChange={(e) => setGenKeyword(e.target.value)}
                placeholder="e.g. dental office construction"
                className="w-full bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50"
              />
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating || !genUrl.trim()}
              className="flex items-center gap-2 px-4 py-2.5 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {generating ? <Loader size={14} className="animate-spin" /> : <Zap size={14} />}
              {generating ? "Generating AI suggestions..." : "Generate Alt Text & Filenames"}
            </button>
          </div>

          {genResults && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-400">
                  {genResults.count} suggestions generated
                </div>
                {genResults.count > 0 && (
                  <button
                    onClick={handleSaveGenerated}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-green-900/30 text-green-400 text-sm rounded-lg hover:bg-green-900/50 disabled:opacity-50"
                  >
                    {saving ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
                    Save All as Fixes
                  </button>
                )}
              </div>

              {(genResults.results || []).map((r, i) => (
                <div key={i} className="bg-dark-300 border border-dark-50 rounded-xl p-4">
                  <div className="flex gap-4">
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-dark-50">
                      <img
                        src={r.image_url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      {/* Alt text row */}
                      {r.suggested_alt_text && (
                        <div className="flex items-start gap-2">
                          <span className="text-xs bg-blue-900/20 text-blue-400 px-1.5 py-0.5 rounded flex-shrink-0">Alt</span>
                          <span className="text-sm text-green-400">{r.suggested_alt_text}</span>
                        </div>
                      )}
                      {/* Filename row */}
                      <div className="flex items-start gap-2">
                        <span className="text-xs bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded flex-shrink-0">File</span>
                        <span className={`text-sm font-mono ${r.bad_filename ? "text-red-400" : "text-gray-400"}`}>
                          {r.filename}
                        </span>
                      </div>
                      {r.suggested_filename && (
                        <div className="flex items-start gap-2">
                          <span className="text-xs bg-amber-900/20 text-amber-400 px-1.5 py-0.5 rounded flex-shrink-0">New</span>
                          <span className="text-sm text-green-400 font-mono">{r.suggested_filename}</span>
                        </div>
                      )}
                      {r.confidence_score > 0 && (
                        <div className="text-xs text-gray-500">Confidence: {r.confidence_score}%</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
